import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { EyeCloseIcon, EyeIcon } from "../../icons";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import Button from "../ui/button/Button";
import Alert from "../ui/alert/Alert";
import { Modal } from "../ui/modal";
import { useModal } from "../../hooks/useModal";
import { request, cache } from "../../utils";
import ResetPasswordModal from "../ui/modal/ResetPasswordModal";
import VerifyAccountModal from "../ui/modal/VerifyAccountModal";

interface tokenInt {
  token: string;
  kybStatus: string;
}

export default function SignInForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const navigate = useNavigate();

  // ---- Modal hooks
  const {
    isOpen: isForgotPasswordOpen,
    openModal: openForgotPassword,
    closeModal: closeForgotPassword,
  } = useModal();

  const {
    isOpen: isResetPasswordOpen,
    openModal: openResetPassword,
    closeModal: closeResetPassword,
  } = useModal();

  const closeForgotModal = () => {
    closeForgotPassword();
    setForgotPasswordEmail("");
    setError("");
  };

  // ---- Handle Login Function.
  const handleLogin = async (e: React.FormEvent) => {
    try {
      e.preventDefault();
      setLoading(true);
      setError("");

      const response = await request.post<tokenInt>({
        url: "/user/login",
        data: { email, password },
      });

      cache.set("TOKEN", response?.data?.token);
      cache.set("USER", response?.data);

      // Check KYB Status if its Approved or pending
      if (
        response?.data?.kybStatus === "PENDING" ||
        response?.data?.kybStatus === "REVIEWING" ||
        response?.data?.kybStatus === "REJECTED"
      ) {
        cache.del("TOKEN");
        cache.del("USER");
        navigate("/kyb/verification/status", { state: { kybStatus: response?.data?.kybStatus } });
        return;
      }
      navigate("/dashboard");
    } catch (err: any) {
      const apiError = err;

      if (
        apiError?.httpStatusCode === 403 &&
        apiError?.errorCode === 104 &&
        apiError?.message?.includes("activate")
      ) {
        setShowOtpModal(true);
        return;
      }

      // Fallback to showing error
      setError(apiError?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // ---- handle sending OTP to mail
  const handleResetPassword = async (e: React.FormEvent) => {
    try {
      e.preventDefault();
      setForgotLoading(true);
      setError("");

      await request.post({
        url: "/user/forgot-password",
        data: { email: forgotPasswordEmail },
      });

      cache.set("RESET_EMAIL", forgotPasswordEmail);
      closeForgotModal();
      openResetPassword();
    } catch (error) {
      setError((error as Error).message);
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="flex flex-col flex-1">
      <div className="w-full max-w-md pt-10 mx-auto"></div>
      <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
        <div>
          <div className="mb-5 sm:mb-8">
            <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
              Sign In
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Enter your email and password to sign in!
            </p>
          </div>

          {error && (
            <div className="mb-6">
              <Alert
                variant="error"
                title="Error"
                message={error}
                showLink={false}
              />
            </div>
          )}

          <div>
            <form onSubmit={handleLogin}>
              <div className="space-y-6">
                <div>
                  <Label>
                    Email <span className="text-error-500">*</span>
                  </Label>
                  <Input
                    type="email"
                    placeholder="info@gmail.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>
                    Password <span className="text-error-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                    >
                      {showPassword ? (
                        <EyeIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                      ) : (
                        <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                      )}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={openForgotPassword}
                    className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                  >
                    Forgot password?
                  </button>
                </div>
                <div>
                  <Button
                    className="w-full"
                    size="sm"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? "Signing in..." : "Sign in"}
                  </Button>
                </div>
              </div>
            </form>

            <div className="mt-5">
              <p className="text-sm font-normal text-center text-gray-700 dark:text-gray-400 sm:text-start">
                Don&apos;t have an account? {""}
                <Link
                  to="/signup"
                  className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
                >
                  Register
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Modal
        isOpen={isForgotPasswordOpen}
        onClose={closeForgotModal}
        className="max-w-md p-8"
      >
        <form onSubmit={handleResetPassword}>
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
              Forgot Password
            </h2>

            {error && (
              <div className="mb-6">
                <Alert
                  variant="error"
                  title="Error"
                  message={error}
                  showLink={false}
                />
              </div>
            )}

            <div>
              <Label>
                Email <span className="text-error-500">*</span>
              </Label>
              <Input
                placeholder="info@gmail.com"
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <Button className="w-full" size="sm" type="submit">
                {forgotLoading ? "sending..." : "Send OTP"}
              </Button>
            </div>
          </div>
        </form>
      </Modal>

      {/* New user Verification of OTP */}
      {showOtpModal && (
        <VerifyAccountModal
          isOpen={showOtpModal}
          onClose={() => setShowOtpModal(false)}
          email={email.toLowerCase().trim()}
        />
      )}

      {/* Forgot Password Modal */}
      <ResetPasswordModal
        isOpen={isResetPasswordOpen}
        onClose={closeResetPassword}
      />
    </div>
  );
}
